import { useEffect, useRef } from "react";
import { Category, ContextPage, EventType } from "../constants";
import { safeReadSessionString, safeWriteSessionString } from "../storage";
import { useTrackingEvent } from "./useTrackingEvent";

const seenKey = (publicationId) =>
  `tracking.reviewRead.seen.v1.${String(publicationId || "")}`;

export const useReviewReadTracking = ({
  publicationId,
  establishmentId,
  enabled = true,
  page = ContextPage.PUBLICATION_DETAILS,
}) => {
  const { runtime, trackEvent } = useTrackingEvent();
  const ref = useRef(null);
  const timerRef = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !publicationId || !establishmentId) return undefined;

    const seen = safeReadSessionString(seenKey(publicationId), null);
    if (seen === "1") {
      firedRef.current = true;
      return undefined;
    }

    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        const visibleEnough = entry.isIntersecting && entry.intersectionRatio >= 0.5;

        if (!visibleEnough) {
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
          return;
        }

        if (firedRef.current || timerRef.current) return;

        timerRef.current = window.setTimeout(() => {
          timerRef.current = null;
          if (firedRef.current) return;
          if (safeReadSessionString(seenKey(publicationId), null) === "1") {
            firedRef.current = true;
            return;
          }

          firedRef.current = true;
          safeWriteSessionString(seenKey(publicationId), "1");

          trackEvent({
            schemaVersion: runtime.schemaVersion,
            eventTime: new Date().toISOString(),
            category: Category.MARKETING_EVENT,
            eventType: EventType.REVIEW_READ,
            establishmentId,
            publicationId,
            visitorCountry: runtime.visitorCountry?.countryCode || undefined,
            context: {
              source: runtime.trackingSource,
              page,
            },
          }).catch(() => {});
        }, 1000);
      },
      { threshold: [0, 0.5, 1] }
    );

    observer.observe(el);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      observer.disconnect();
    };
  }, [enabled, establishmentId, page, publicationId, runtime, trackEvent]);

  return { ref };
};
